import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Pagination from "../components/Pagination";
import { Helmet } from "react-helmet";
import "../styles/Products.css";

function Products() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(8);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ================= FETCH PRODUCTS ================= */
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/products`
      );
      setProducts(response.data.products);
      setFilteredProducts(response.data.products);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching products:", err.message);
      setError("Failed to fetch products. Please try again later.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  /* ================= READ URL → STATE ================= */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const category = params.get("category");
    const search = params.get("search");
    const page = params.get("page");

    if (category) setSelectedCategory(category);
    if (search) setSearchTerm(search);
    if (page) setCurrentPage(Number(page));
  }, [location.search]);

  /* ================= FILTER LOGIC ================= */
  useEffect(() => {
    let filtered = products;

    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        (product) => product.category === selectedCategory
      );
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [products, searchTerm, selectedCategory]);

  /* ================= WRITE STATE → URL ================= */
  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedCategory !== "All") params.set("category", selectedCategory);
    if (searchTerm) params.set("search", searchTerm);
    if (currentPage > 1) params.set("page", currentPage);

    navigate({ search: params.toString() }, { replace: true });
  }, [selectedCategory, searchTerm, currentPage, navigate]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleProductClick = (id) => {
    navigate(`/products/${id}`);
  };

  return (
    <div className="products-container">
      {/* ✅ Helmet UNCHANGED */}
      <Helmet>
        <title>Our Products – Relison Pharma</title>
        <meta
          name="description"
          content="Browse Relison Pharma’s product range of high-quality and trusted medicines. Search, explore, and discover pharmaceutical solutions for better health."
        />
        <meta
          name="keywords"
          content="relison pharma products, medicines, healthcare, drug list, pharmaceutical catalog"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Relison Pharma" />
        <meta property="og:title" content="Our Products – Relison Pharma" />
        <meta property="og:description" content="Explore our wide range of pharma products at Relison Pharma. Trusted healthcare and quality medicines." />
        <meta property="og:image" content="https://relisonpharma.com/images/product-thumbnail.jpg" />
        <meta property="og:url" content="https://relisonpharma.com/products/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Products – Relison Pharma" />
        <meta name="twitter:description" content="Trusted pharmaceutical products from Relison Pharma. Search and browse our catalog." />
        <meta name="twitter:image" content="https://relisonpharma.com/images/product-thumbnail.jpg" />
      </Helmet>

      <h1>Our Products</h1>

      <div className="products-wrapper">
        {/* LEFT – CATEGORY FILTER */}
        <aside className="products-sidebar">
          <h3 className="sidebar-title">Categories</h3>

          {[
            { name: "All", value: "All", icon: "📦" },
            { name: "Tablets", value: "Tablets", icon: "🟣" },
            { name: "Capsules", value: "Capsules", icon: "💊" },
            { name: "Syrups", value: "Syrups", icon: "🧴" },
            { name: "SoftGel", value: "SoftGel", icon: "🧬" },
            { name: "Injectables", value: "Injectables", icon: "💉" },
          ].map((cat) => (
            <button
              key={cat.value}
              className={`sidebar-category-btn ${
                selectedCategory === cat.value ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(cat.value)}
            >
              <span className="category-icon">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </aside>

        {/* RIGHT – SEARCH + PRODUCTS */}
        <main className="products-main">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          {isLoading ? (
            <p>Loading products...</p>
          ) : (
            <>
              <div className="products-grid">
                {currentProducts.length > 0 ? (
                  currentProducts.map((product) => (
                    <div
                      key={product._id}
                      className="product-card"
                      onClick={() => handleProductClick(product._id)}
                    >
                      <img
                        src={
                          product.image.startsWith("http")
                            ? product.image
                            : `https://res.cloudinary.com/ddv0dhxf6/image/upload/relison-products/${product.image
                                .split("\\")
                                .pop()}`
                        }
                        alt={product.name}
                      />
                      <h3>{product.name}</h3>
                    </div>
                  ))
                ) : (
                  <p>No products found.</p>
                )}
              </div>

              <Pagination
                totalProducts={filteredProducts.length}
                productsPerPage={productsPerPage}
                paginate={paginate}
                currentPage={currentPage}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default Products;
